import { FC, useRef, useState } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { useModal } from "contexts/modalContext/modalContext";
import { useSaveInboxMail } from "hooks/useSaveInboxMail";
import { MailDataI } from "models/models";
import useJumboFormsCall from "hooks/useJumboFormsCall";
import useRemoveMailFunction from "hooks/useRemoveMailFunction";
import useInbox from "hooks/useInbox.ts";
import useOnClickOutside from "hooks/useOnClickOutside";
import EditEmailModal from "components/Reusable/EditEmailModal/EditEmailModal";
import Icon from "components/Elements/Icon/Icon";
//@ts-ignore
import config from "config";
import { LogModal } from "../Embed/LogModal.js";
import useRemoveExpiredMailAction from "./useRemoveExpiredMailAction.ts";
import MobileBottomMenuContainer from "../Popups/MobileBottomMenuContainer.tsx";
import LabelAs from "../Popups/LabelAs.tsx";
import RemoveMailFiles from "../Popups/RemoveMailFiles.tsx";
import { useToasts } from "react-toast-notifications";
import usePickerQueryParamActions from "../Picker/usePickerQueryParamActions.ts";
import { useUser } from "contexts/userContext/userContext.js";

type SidebarPayloadT = {
  name: string;
  data: MailDataI;
};

type MobileActionListPopupT = {
  data: MailDataI;
  setShowSideBar: (payload: SidebarPayloadT) => void;
};

const MobileActionListPopup: FC<MobileActionListPopupT> = ({
  data,
  setShowSideBar,
}) => {
  const { getPath, getTextByKey } = useContent();
  const { getUserData } = useUser();
  const { setModal, closeModal } = useModal();
  const [removeLoad, setRemoveLoad] = useState(false);
  const { getSearchParams, setMailDataProp, removeMailFromState } = useInbox();
  const { addToast } = useToasts();
  const { handleClick, isSaved, loading } = useSaveInboxMail({
    mailData: data,
  });
  const { apiCall, ftpCall } = useJumboFormsCall({ mailData: data });
  const removeMailFunc = useRemoveMailFunction({
    activeArray: [data.id],
    status: data.mailStatus,
    setLoading: setRemoveLoad,
    filesSize: data.filesSize,
  });
  const [showList, setShowList] = useState(false);
  const actionsRef = useRef(null);
  useOnClickOutside(actionsRef, () => setShowList(false));
  const removeExpiredMailAction = useRemoveExpiredMailAction();
  const [showLabelAs, setShowLabelAs] = useState(false);
  const { openIframe } = usePickerQueryParamActions();

  const removeCompletelyCb = () => {
    setTimeout(() => {
      removeMailFromState(data.id);
      closeModal();
    }, 300);
  };

  const convertToPickerAction = getUserData()?.HideJumboPicker
    ? null
    : {
        name: "convert-to-picker",
        iconName: "newInbox/Picker/picker-gray",
        textKey: "tooltip-action-convert-to-picker-text",
        action: () => {
          if (data?.canConvertToPicker) {
            if (data.isSaved && !data.isSavedByUser) {
              addToast(getTextByKey("converting-disabled-notification"), {
                appearance: "error",
              });
            } else {
              openIframe("convert", {
                mailId: data.id,
              });
            }
          } else {
            addToast(getTextByKey("converting-not-allowed-no-images"), {
              appearance: "error",
            });
          }
        },
      };

  const removedActionsData = [
    {
      name: "alog",
      iconName: "alog",
      textKey: "tooltip-action-alog-text",
      action: () => {
        setModal({
          modal: LogModal,
          props: { deliveryNumber: data.deliveryNumber },
        });
      },
    },
    {
      ...removeExpiredMailAction(data, removeCompletelyCb),
      textKey: "tooltip-action-remove-mail-completely-text",
    },
  ];

  const popupActionsGroup =
    data.mailStatus === "pendingRemoved"
      ? [
          {
            icon: removeLoad ? null : "newInbox/Actions/retreive-deletion",
            iconSrc: removeLoad ? loader : null,
            textKey: `tooltip-action-unremove-mail-text`,
            action: () => {
              if (data.mailStatus === "pendingRemoved") {
                removeMailFunc();
              } else {
                setModal({
                  modal: RemoveMailFiles,
                  modalSize: "full-modal",
                  props: {
                    confirm: removeMailFunc,
                  },
                });
              }
            },
          },
        ]
      : data.mailStatus === "removed"
      ? removedActionsData
      : [
          {
            icon: "edit-pen-solid",
            textKey: "tooltip-action-edit-email-text",
            action: () => {
              setModal({
                modal: EditEmailModal,
                props: {
                  subject: data.subject,
                  message: data.text,
                  emailId: data.id,
                  userNameDisplay: data.displaySenderName,
                  subjectDisplay: data.displayEmailSubject,
                  callbackUpdateProps: ({
                    subjectValue,
                    messageValue,
                    displaySenderName,
                    displaySubject,
                  }: any) => {
                    setMailDataProp(
                      data.id,
                      [
                        "subject",
                        "text",
                        "displaySenderName",
                        "displayEmailSubject",
                      ],
                      [
                        subjectValue,
                        messageValue,
                        displaySenderName,
                        displaySubject,
                      ]
                    );
                  },
                },
              });
            },
          },
          {
            icon: loading ? null : isSaved ? "unsave-solid" : "save-solid",
            iconSrc: loading ? loader : null,
            textKey: `tooltip-action-${isSaved ? "un" : ""}save-mail-text`,
            action: () => {
              handleClick();
            },
          },
          ...(getSearchParams().f === "jform"
            ? [
                {
                  icon: "api",
                  textKey: `tooltip-action-api-text`,
                  action: () => {
                    apiCall();
                  },
                },
                {
                  icon: "ftp",
                  textKey: `tooltip-action-ftp-text`,
                  action: () => {
                    ftpCall();
                  },
                },
              ]
            : [
                {
                  icon: getUserData()?.HideJumboPay ? null : "jumbopay",
                  textKey: `tooltip-action-mail-payment-text`,
                  action: () => {
                    setShowSideBar({ name: "jumbopay", data });
                  },
                },
              ]),
          {
            icon: "brand",
            textKey: `tooltip-action-mail-branding-text`,
            action: () => {
              window.open(
                `${config!.baseurl}${getPath()}gallery/${data.id}/select-cover`
              );
            },
          },
          (getSearchParams()?.t === "received" ? !!data?.isJForm : true)
            ? convertToPickerAction
            : null,
          {
            icon: removeLoad ? null : "newInbox/Actions/remove-files",
            iconSrc: loading ? loader : null,
            textKey: `tooltip-action-remove-mail-text`,
            action: () => {
              if (data.mailStatus === "pendingRemoved") {
                removeMailFunc();
              } else {
                setModal({
                  modal: RemoveMailFiles,
                  modalSize: "full-modal",
                  props: {
                    confirm: removeMailFunc,
                  },
                });
              }
            },
          },
        ];

  return (
    <>
      <MobileBottomMenuContainer
        setShowSidebar={setShowLabelAs}
        showSidebar={showLabelAs}
      >
        <LabelAs onClose={setShowLabelAs} selectedMails={[data.id]} builtIn />
      </MobileBottomMenuContainer>
      <div className="additional-actions-wrapper" ref={actionsRef}>
        <Icon
          name="vertical-three-dots"
          darkGray
          onClick={() => {
            setShowList((prev) => !prev);
          }}
        />
        {/* <CustomPopup showState={showList} topPosition={40}> */}
        {showList && (
          <div className="additional-actions-popup">
            <div
              className="action-container"
              onClick={() => setShowLabelAs(true)}
            >
              <Icon name="newInbox/label" gray />
              {getTextByKey("label-as")}
            </div>
            {(popupActionsGroup as any)
              .filter((a: any) => !!a)
              .map((action: any) => {
                return (
                  <div
                    className="action-container"
                    key={action.textKey}
                    onClick={action?.action || action?.clickAction}
                  >
                    <Icon
                      name={action?.icon || action?.iconName}
                      src={action?.iconSrc}
                      gray
                    />{" "}
                    {getTextByKey(action.textKey)}{" "}
                  </div>
                );
              })}
          </div>
        )}
        {/* </CustomPopup> */}
      </div>
    </>
  );
};

export default MobileActionListPopup;
